import { inject, isDevMode } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { AccountService } from './account.service';
import { StateStorageService } from './state-storage.service';
import { LoginService } from '../login/login.service';
import { AppConfigService } from '../config/app-config.service';
import { CandidateSessionService } from './candidate-session.service';

/**
 * Functional route guard (Angular 21 CanActivateFn pattern).
 * Replaces the old class-based UserRouteAccessService.
 *
 * Checks authentication and feature-based authorization.
 * Uses AppConfigService.microsoftLogin to determine redirect behavior.
 */
export const userRouteAccessGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean> => {
  const accountService = inject(AccountService);
  const stateStorageService = inject(StateStorageService);
  const loginService = inject(LoginService);
  const appConfigService = inject(AppConfigService);
  const candidateSession = inject(CandidateSessionService);
  const router = inject(Router);

  const features: string[] | undefined = route.data['features'];

  try {
    const account = await accountService.identity();

    // If microsoftLogin and no features required, allow access
    if (appConfigService.microsoftLogin && (!features || features.length === 0)) {
      return true;
    }

    stateStorageService.storeUrl(state.url);

    if (account) {
      if (appConfigService.microsoftLogin) {
        // Microsoft login: check feature-based authorization
        const hasFeature = accountService.hasAnyFeature(features!);
        if (hasFeature) {
          return true;
        }
        if (isDevMode()) {
          console.warn(`[userRouteAccessGuard] Access denied for features: ${features}`);
        }
        router.navigate(['accessdenied']);
        return false;
      } else {
        // LDAP login: allow access if authenticated
        return true;
      }
    } else {
      // Not authenticated - redirect to login. KCS-565 Part 2: a candidate session (its own JWT) must go to
      // the app /login page, never the Microsoft redirect, even on MSL instances.
      appConfigService.microsoftLogin && !candidateSession.isActive()
        ? loginService.login()
        : router.navigate(['/login']);
      return false;
    }
  } catch {
    return false;
  }
};
